/**
 * Location filter URL-param model, shared by the server page (parsing) and
 * the client filter components (writing).
 *
 * Params:
 *   near=<lat>,<lng>  centre point, 4-decimal rounded (~11 m) so geolocation
 *                     jitter doesn't fragment URLs or the SSR cache key space
 *   radius=<km>       one of RADIUS_OPTIONS_KM; defaults to DEFAULT_RADIUS_KM
 *                     when `near` is present
 *   city=<label>      display label for the chip/summary ("Lausanne") — never
 *                     used for matching, only for rendering
 *
 * Raw lat/lng (not a Google placeId) keeps URLs shareable without a Places
 * round-trip on every SSR render.
 */

export const RADIUS_OPTIONS_KM = [2, 5, 10, 20, 40, 60, 100, 250] as const;

export const DEFAULT_RADIUS_KM = 40;

export interface NearPoint {
  lat: number;
  lng: number;
}

/** Round to 4 decimals (~11 m) — the canonical precision for `near`. */
export function roundCoord(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/** Serialize a centre point into the `near` param value. */
export function formatNear(point: NearPoint): string {
  return `${roundCoord(point.lat)},${roundCoord(point.lng)}`;
}

/**
 * Parse the `near` param. Returns undefined for absent/malformed/out-of-range
 * values (the filter is simply inactive — never an error page).
 */
export function parseNear(input: string | undefined): NearPoint | undefined {
  if (!input) return undefined;
  const [latRaw, lngRaw, ...rest] = input.split(',');
  if (rest.length > 0 || latRaw === undefined || lngRaw === undefined) {
    return undefined;
  }
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return undefined;
  return { lat: roundCoord(lat), lng: roundCoord(lng) };
}

/**
 * Parse the `radius` param against the allow-list. Falls back to
 * DEFAULT_RADIUS_KM (only meaningful when `near` is present — callers gate
 * on that).
 */
export function parseRadius(input: string | undefined): number {
  const n = Number(input);
  return (RADIUS_OPTIONS_KM as readonly number[]).includes(n)
    ? n
    : DEFAULT_RADIUS_KM;
}
